import { FC } from 'react';
import {
  Collapse,
  Flex,
  InputGroup,
  InputRightElement,
  useBreakpointValue,
  useDisclosure,
} from '@chakra-ui/react';

import Chip from 'components/_core/Chip';
import IconButton from 'components/_core/IconButton';
import SearchBar from 'components/SearchBar';
import { logEvent } from 'features/Analytics/analytics';
import { useTransactionsFilters } from 'features/Bank/pages/Overview/hooks/useTransactionsFilters';

import AdvancedFilters from './components/AdvancedFilters';
import locales from './locales';

interface AdvancedSearchProps {
  totalCount: number;
}

const AdvancedSearch: FC<AdvancedSearchProps> = ({ totalCount }) => {
  const { isOpen, onToggle } = useDisclosure();
  const { filters, filtersCount, updateFilters } = useTransactionsFilters();

  const isExtraLarge = useBreakpointValue({ base: false, xl: true });
  const placeholder = useBreakpointValue({
    base: locales.placeholder.short,
    lg: locales.placeholder.long,
  });

  const onSearch = (q: string) => updateFilters({ ...filters, q });

  const onFilterButtonClick = () => {
    logEvent({
      name: `Transactions Filters Panel ${isOpen ? 'Closed' : 'Opened'}`,
      usePrefix: false,
    });

    onToggle();
  };

  return (
    <>
      <InputGroup>
        <SearchBar
          onChange={onSearch}
          placeholder={placeholder}
          value={filters.q ?? ''}
          {...(isOpen && { borderBottomRadius: '0' })}
        />

        <InputRightElement padding="0" right={isOpen ? '0' : '1'}>
          {filters.q ? (
            <IconButton
              aria-label={locales.clear}
              icon="cross"
              onClick={() => onSearch('')}
              variant="inline-secondary"
            />
          ) : null}

          <Chip
            {...(!isExtraLarge && {
              icon: 'filter',
            })}
            iconRight={isOpen ? 'chevron-up' : 'chevron-down'}
            isActive={isOpen}
            onClick={onFilterButtonClick}
            {...(isOpen
              ? {
                  borderRadius: '0',
                  borderTopRightRadius: 'radius-6',
                }
              : {
                  borderBottomColor: 'transparent',
                  borderLeftRadius: '0',
                  borderRightColor: 'transparent',
                  borderTopColor: 'transparent',
                  height: 'calc(100% - 2px)',
                })}
          >
            <Flex alignItems="center" display="flex" gap="space-4">
              {isExtraLarge ? locales.filters : null}
              {filtersCount > 0 ? ` (${filtersCount})` : null}
            </Flex>
          </Chip>
        </InputRightElement>
      </InputGroup>

      <Collapse in={isOpen}>
        <AdvancedFilters totalCount={totalCount} />
      </Collapse>
    </>
  );
};

export default AdvancedSearch;
